/**
 * Digital Experience — private, non-commercial page for a booking.
 *
 * CRITICAL: this page never renders prices, discounts, availability,
 * calendars, checkout, booking buttons or links to commercial tour pages.
 * The only credential is the internal access code (MM-XXXXXXX).
 */
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useRef, useState } from "react";
import {
  Headphones, Download, BookOpen, MapPin, Clock, Users, CalendarDays,
  Info, Backpack, Play, Pause, Lock, Loader2, CheckCircle2,
} from "lucide-react";

import { getExperience } from "@/lib/experience.functions";
import type { ExperienceAsset, ExperienceDTO, ExperienceLang } from "@/lib/experience.server";
import { Logo } from "@/components/site/Logo";
import { PhotoUpload } from "@/components/audioguide/PhotoUpload";
import { guideStrings } from "@/lib/audio-guide-i18n";
import { Skeleton } from "@/components/ui/skeleton";

const experienceQuery = (code: string, lang: string | undefined) =>
  queryOptions({
    queryKey: ["experience", code, lang ?? "auto"],
    queryFn: () => getExperience({ data: { code, ...(lang ? { lang: lang as ExperienceLang } : {}) } }),
    staleTime: 30_000,
  });

export const Route = createFileRoute("/experiencia/$code")({
  head: () => ({
    meta: [
      { title: "Tu Experiencia Digital · Mostrando México" },
      {
        name: "description",
        content:
          "Accede a la audioguía, la guía en PDF y la información importante de tu experiencia con Mostrando México.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Tu Experiencia Digital · Mostrando México" },
      {
        property: "og:description",
        content: "Audioguía, guía PDF, punto de encuentro y recomendaciones para tu tour.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ExperiencePage,
  errorComponent: () => <Shell><Empty title="No pudimos cargar tu experiencia" body="Vuelve a intentarlo en unos segundos." /></Shell>,
  notFoundComponent: () => <Shell><Empty title="Enlace no válido" body="Revisa el enlace que recibiste o escríbenos por WhatsApp." /></Shell>,
});

const LABELS: Record<ExperienceLang, Record<string, string>> = {
  es: {
    welcome: "¡Bienvenido a tu experiencia con Mostrando México!", yours: "Tu experiencia",
    audio: "Audioguía", pdf: "Guía en PDF", info: "Información importante",
    meeting: "Punto de encuentro", times: "Horarios", bring: "Qué llevar",
    download: "Descargar", downloaded: "Descargado", play: "Escuchar", pause: "Pausar",
    used: "descarga utilizada", travelers: "viajeros",
    limit: "Este archivo ya fue descargado para esta reserva. Puedes continuar escuchándolo online.",
    blocked: "Descarga bloqueada por el equipo. Puedes continuar escuchándolo online.",
    pdfDesc: "Guía completa del destino, lista para imprimir.", online: "Reproducción online ilimitada",
  },
  en: {
    welcome: "Welcome to your experience with Mostrando México!", yours: "Your experience",
    audio: "Audio guide", pdf: "PDF guide", info: "Important information",
    meeting: "Meeting point", times: "Times", bring: "What to bring",
    download: "Download", downloaded: "Downloaded", play: "Listen", pause: "Pause",
    used: "download used", travelers: "travelers",
    limit: "This file was already downloaded for this booking. You can keep listening online.",
    blocked: "Download blocked by our team. You can keep listening online.",
    pdfDesc: "Complete destination guide, ready to print.", online: "Unlimited online playback",
  },
  fr: {
    welcome: "Bienvenue dans ton expérience avec Mostrando México !", yours: "Ton expérience",
    audio: "Audioguide", pdf: "Guide PDF", info: "Informations importantes",
    meeting: "Point de rencontre", times: "Horaires", bring: "À emporter",
    download: "Télécharger", downloaded: "Téléchargé", play: "Écouter", pause: "Pause",
    used: "téléchargement utilisé", travelers: "voyageurs",
    limit: "Ce fichier a déjà été téléchargé pour cette réservation. Tu peux continuer à l'écouter en ligne.",
    blocked: "Téléchargement bloqué par notre équipe. Tu peux continuer à l'écouter en ligne.",
    pdfDesc: "Guide complet de la destination, prêt à imprimer.", online: "Écoute en ligne illimitée",
  },
  pt: {
    welcome: "Bem-vindo à sua experiência com Mostrando México!", yours: "Sua experiência",
    audio: "Audioguia", pdf: "Guia em PDF", info: "Informações importantes",
    meeting: "Ponto de encontro", times: "Horários", bring: "O que levar",
    download: "Baixar", downloaded: "Baixado", play: "Ouvir", pause: "Pausar",
    used: "download utilizado", travelers: "viajantes",
    limit: "Este arquivo já foi baixado para esta reserva. Você pode continuar ouvindo online.",
    blocked: "Download bloqueado pela equipe. Você pode continuar ouvindo online.",
    pdfDesc: "Guia completo do destino, pronto para imprimir.", online: "Reprodução online ilimitada",
  },
  it: {
    welcome: "Benvenuto nella tua esperienza con Mostrando México!", yours: "La tua esperienza",
    audio: "Audioguida", pdf: "Guida PDF", info: "Informazioni importanti",
    meeting: "Punto d'incontro", times: "Orari", bring: "Cosa portare",
    download: "Scarica", downloaded: "Scaricato", play: "Ascolta", pause: "Pausa",
    used: "download utilizzato", travelers: "viaggiatori",
    limit: "Questo file è già stato scaricato per questa prenotazione. Puoi continuare ad ascoltarlo online.",
    blocked: "Download bloccato dal team. Puoi continuare ad ascoltarlo online.",
    pdfDesc: "Guida completa della destinazione, pronta da stampare.", online: "Ascolto online illimitato",
  },
  de: {
    welcome: "Willkommen zu deinem Erlebnis mit Mostrando México!", yours: "Dein Erlebnis",
    audio: "Audioguide", pdf: "PDF-Guide", info: "Wichtige Informationen",
    meeting: "Treffpunkt", times: "Zeiten", bring: "Was mitnehmen",
    download: "Herunterladen", downloaded: "Heruntergeladen", play: "Anhören", pause: "Pause",
    used: "Download verwendet", travelers: "Reisende",
    limit: "Diese Datei wurde für diese Buchung bereits heruntergeladen. Du kannst sie weiter online hören.",
    blocked: "Download vom Team gesperrt. Du kannst weiter online hören.",
    pdfDesc: "Vollständiger Reiseführer, druckfertig.", online: "Unbegrenztes Online-Hören",
  },
};

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-center px-4 py-3">
          <Logo className="h-8 w-auto" />
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 pb-24 pt-6 sm:px-6">{children}</main>
    </div>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="py-24 text-center">
      <h1 className="font-display text-3xl">{title}</h1>
      <p className="mt-2 text-muted-foreground">{body}</p>
    </div>
  );
}

function ExperiencePage() {
  return (
    <Shell>
      <Suspense fallback={<div className="space-y-4 py-8"><Skeleton className="h-40 w-full rounded-3xl" /><Skeleton className="h-64 w-full rounded-3xl" /></div>}>
        <Body />
      </Suspense>
    </Shell>
  );
}

function Body() {
  const { code } = Route.useParams();
  const [lang, setLang] = useState<string | undefined>(undefined);
  const { data } = useSuspenseQuery(experienceQuery(code, lang));
  const exp = data.experience;

  if (!exp) {
    return <Empty title="Enlace no válido" body="Revisa el enlace que recibiste o escríbenos por WhatsApp." />;
  }
  return <Experience exp={exp} code={code} onLang={setLang} />;
}

function Experience({ exp, code, onLang }: { exp: ExperienceDTO; code: string; onLang: (l: string) => void }) {
  const L = LABELS[exp.booking.lang] ?? LABELS.es;
  const hero = exp.tour.images[0] ?? null;

  const dateText = useMemo(() => {
    if (!exp.booking.travelDate) return null;
    try {
      return new Date(`${exp.booking.travelDate}T12:00:00`).toLocaleDateString(exp.booking.lang, {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
    } catch {
      return exp.booking.travelDate;
    }
  }, [exp.booking.travelDate, exp.booking.lang]);

  return (
    <div className="space-y-8">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-[20px] border border-border/70 bg-card shadow-[0_10px_40px_-24px_rgba(0,0,0,0.35)]">
        {hero && (
          <div className="relative h-48 w-full sm:h-64">
            <img src={hero} alt={exp.tour.title} className="h-full w-full object-cover" fetchPriority="high" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
          </div>
        )}
        <div className="relative px-5 pb-6 pt-4 sm:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">{L.yours}</p>
          <h1 className="mt-1 font-display text-2xl leading-tight sm:text-4xl">{exp.tour.title}</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {L.welcome}
          </p>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
            {dateText && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {dateText}</span>}
            <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> {exp.booking.travelers} {L.travelers}</span>
            {exp.tour.durationText && <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {exp.tour.durationText}</span>}
            {exp.tour.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {exp.tour.location}</span>}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground">
              {exp.booking.accessCode}
            </span>
            {exp.langsAvailable.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => onLang(l)}
                className={`rounded-full border px-2.5 py-1 text-xs font-semibold uppercase transition-colors ${
                  l === exp.booking.lang
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:bg-accent"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Audio */}
      <section className="rounded-[20px] border border-border/70 bg-card p-5 sm:p-7">
        <h2 className="inline-flex items-center gap-2 font-display text-xl sm:text-2xl">
          <Headphones className="h-5 w-5 text-primary" /> {L.audio}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{L.online}</p>
        <div className="mt-5 space-y-3">
          {exp.audios.length === 0 && (
            <p className="text-sm text-muted-foreground">—</p>
          )}
          {exp.audios.map((a) => (
            <AudioRow key={a.key} asset={a} code={code} lang={exp.booking.lang} L={L} />
          ))}
        </div>
      </section>

      {/* PDF */}
      {exp.pdf && (
        <section className="rounded-[20px] border border-border/70 bg-card p-5 sm:p-7">
          <h2 className="inline-flex items-center gap-2 font-display text-xl sm:text-2xl">
            <BookOpen className="h-5 w-5 text-primary" /> {L.pdf}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{L.pdfDesc}</p>
          <PdfRow asset={exp.pdf} code={code} lang={exp.booking.lang} L={L} />
        </section>
      )}

      {/* Meeting point + times */}
      {(exp.tour.meetingPoints.length > 0 || exp.tour.startTimes.length > 0) && (
        <section className="grid gap-4 sm:grid-cols-2">
          {exp.tour.meetingPoints.length > 0 && (
            <div className="rounded-[20px] border border-border/70 bg-card p-5">
              <h3 className="inline-flex items-center gap-2 font-display text-lg"><MapPin className="h-4 w-4 text-primary" /> {L.meeting}</h3>
              <ul className="mt-3 space-y-3 text-sm">
                {exp.tour.meetingPoints.map((m, i) => (
                  <li key={i}>
                    <p className="font-semibold">{m.title}</p>
                    {m.address && <p className="text-muted-foreground">{m.address}</p>}
                    {m.latitude != null && m.longitude != null && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${m.latitude},${m.longitude}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-flex text-xs font-semibold text-primary hover:underline"
                      >
                        Google Maps →
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {exp.tour.startTimes.length > 0 && (
            <div className="rounded-[20px] border border-border/70 bg-card p-5">
              <h3 className="inline-flex items-center gap-2 font-display text-lg"><Clock className="h-4 w-4 text-primary" /> {L.times}</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {exp.tour.startTimes.map((t) => (
                  <span key={t} className="rounded-full bg-accent px-3 py-1 text-sm font-semibold text-accent-foreground">{t}</span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Info + recommendations */}
      {(exp.tour.importantInfo || exp.tour.requirements || exp.tour.recommendations || exp.tour.included.length > 0) && (
        <section className="space-y-4">
          {(exp.tour.importantInfo || exp.tour.requirements) && (
            <div className="rounded-[20px] border border-border/70 bg-card p-5 sm:p-7">
              <h3 className="inline-flex items-center gap-2 font-display text-lg"><Info className="h-4 w-4 text-primary" /> {L.info}</h3>
              {exp.tour.importantInfo && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-foreground/85">{exp.tour.importantInfo}</p>}
              {exp.tour.requirements && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-foreground/85">{exp.tour.requirements}</p>}
            </div>
          )}
          {(exp.tour.recommendations || exp.tour.included.length > 0) && (
            <div className="rounded-[20px] border border-border/70 bg-card p-5 sm:p-7">
              <h3 className="inline-flex items-center gap-2 font-display text-lg"><Backpack className="h-4 w-4 text-primary" /> {L.bring}</h3>
              {exp.tour.recommendations && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-foreground/85">{exp.tour.recommendations}</p>}
              {exp.tour.included.length > 0 && (
                <ul className="mt-3 space-y-2 text-sm">
                  {exp.tour.included.map((x, i) => (
                    <li key={i} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {x}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}

      {/* Share your photos — mandatory section with explicit promotional consent */}
      <PhotoUpload
        tourSlug={exp.booking.tourSlug ?? ""}
        lang={exp.booking.lang}
        accessCode={exp.booking.accessCode}
        strings={guideStrings(exp.booking.lang)}
      />
    </div>
  );
}

type Labels = Record<string, string>;

function AudioRow({ asset, code, lang, L }: { asset: ExperienceAsset; code: string; lang: string; L: Labels }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [used, setUsed] = useState(asset.used);

  const canDownload = !asset.blocked && used < asset.allowed;
  const streamUrl = `/api/public/experiencia/audio?code=${encodeURIComponent(code)}&key=${encodeURIComponent(asset.key)}&lang=${lang}&mode=stream`;

  function toggle() {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); return; }
    if (!el.src) el.src = streamUrl;
    void el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }

  async function download() {
    if (!canDownload) { setMsg(asset.blocked ? L.blocked : L.limit); return; }
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(
        `/api/public/experiencia/audio?code=${encodeURIComponent(code)}&key=${encodeURIComponent(asset.key)}&lang=${lang}&mode=download`,
      );
      if (res.status === 409) {
        const reason = await res.text();
        setMsg(reason === "blocked" ? L.blocked : L.limit);
        setUsed(asset.allowed);
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${asset.key}-${lang}.mp3`;
      a.click();
      URL.revokeObjectURL(url);
      setUsed((u) => u + 1);
    } catch {
      setMsg(L.limit);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border border-border/60 bg-background/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold">{asset.title}</p>
          <p className="text-xs text-muted-foreground">
            {asset.description ? `${asset.description} · ` : ""}
            {asset.estimatedSeconds ? `~${Math.round(asset.estimatedSeconds / 60) || 1} min · ` : ""}
            {used}/{asset.allowed} {L.used}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {playing ? L.pause : L.play}
          </button>
          <button
            type="button"
            onClick={download}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-accent disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : canDownload ? <Download className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            {L.download}
          </button>
        </div>
      </div>
      {msg && <p className="mt-2 text-xs text-muted-foreground">{msg}</p>}
      <audio ref={audioRef} preload="none" onEnded={() => setPlaying(false)} className="hidden" />
    </div>
  );
}

function PdfRow({ asset, code, lang, L }: { asset: ExperienceAsset; code: string; lang: string; L: Labels }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [used, setUsed] = useState(asset.used);
  const canDownload = !asset.blocked && used < asset.allowed;

  async function download() {
    if (!canDownload) { setMsg(asset.blocked ? L.blocked : L.limit); return; }
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(`/api/public/experiencia/pdf?code=${encodeURIComponent(code)}&lang=${lang}`);
      if (res.status === 409) {
        const reason = await res.text();
        setMsg(reason === "blocked" ? L.blocked : L.limit);
        setUsed(asset.allowed);
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `guia-${lang}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setUsed((u) => u + 1);
    } catch {
      setMsg(L.limit);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/60 bg-background/60 p-4">
      <p className="text-xs text-muted-foreground">{used}/{asset.allowed} {L.used}</p>
      <button
        type="button"
        onClick={download}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : canDownload ? <Download className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
        {L.download}
      </button>
      {msg && <p className="w-full text-xs text-muted-foreground">{msg}</p>}
    </div>
  );
}
